// src/pages/LiveTrackerPage.tsx — Live Tracker (admin / HR-admin only).
//
// Staff-facing health view of the live.nymr.co.uk GPS tracker fleet:
//   1 Fleet strip — "X of Y trackers online" + per-unit cards (online/offline,
//     since when, heartbeat age, network/signal, satellites)
//   2 Feed health — GPS vendor platform + Real Time Trains (from dbo.PollOutcomes)
//   3 Live trains snapshot — the public map's /api/live/state via the
//     same-origin /api/live-tracker/state proxy
//   4 On/off history — dbo.TrackerEvents, last 7 days
// Polls /health + /state every 30s while open. All times shown UK local
// (Europe/London). Prominent link out to the public live map.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Hairline, PermissionLoading, Stamp, TopBar } from "../components/ui";

const LIVE_MAP_URL = "https://live.nymr.co.uk";
const POLL_MS = 30_000;
const FEED_STALE_MINUTES = 20; // feed considered failing after this long without a successful poll

// ── /api/live-tracker/health ────────────────────────────────────────────────
interface UnitRow {
  unitId: number; unitName: string; isOnline: boolean;
  lastHeartbeatUtc: string | null; lastFixUtc: string | null;
  onlineSinceUtc: string | null; offlineSinceUtc: string | null; lastPolledUtc: string | null;
  network: string | null; signalPct: number | null; satellites: number | null; vendorActive: boolean | null;
}
interface EventRow {
  unitId: number; unitName: string; eventType: string; eventUtc: string | null;
  heartbeatAgeSeconds: number | null; details: string | null;
}
interface FeedRow {
  lastAttemptUtc: string | null; lastSuccessUtc: string | null;
  lastOk: boolean | null; lastErrorType: string | null; lastMessage: string | null;
}
interface HealthResponse {
  summary: { onlineUnits: number; totalUnits: number };
  units: UnitRow[];
  events: EventRow[];
  feed: { tracker: FeedRow; rtt: FeedRow };
  generatedAt: string;
}

// ── /api/live-tracker/state (live.nymr.co.uk /api/live/state, verbatim) ────
interface StateStation { name?: string; positionPct?: number }
interface StateTrain {
  id?: string; locoCode?: string | null; matchedHeadcode?: string | null;
  direction?: string | null; positionPct?: number | null; status?: string | null;
  delayMinutes?: number | null; source?: string | null; isEstimated?: boolean;
  lastUpdatedUtc?: string | null; staleSeconds?: number | null;
}
interface StateResponse {
  generatedUtc?: string; ukTime?: string; mode?: string; connection?: string;
  stations?: StateStation[]; trains?: StateTrain[];
}

// ── UK-local time formatting (payload timestamps are UTC) ───────────────────
const fmtDateTime = (isoStr: string | null | undefined): string =>
  isoStr ? new Date(isoStr).toLocaleString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtTime = (isoStr: string | null | undefined): string =>
  isoStr ? new Date(isoStr).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" }) : "—";

function durationSince(isoStr: string | null | undefined, now: number): string | null {
  if (!isoStr) return null;
  const mins = Math.max(0, Math.round((now - new Date(isoStr).getTime()) / 60_000));
  if (mins < 1) return "under a minute";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)} days`;
}

function ageText(isoStr: string | null | undefined, now: number): string {
  const d = durationSince(isoStr, now);
  return d ? `${d} ago` : "never";
}

// ── small presentational bits (house style) ─────────────────────────────────
function OnlinePill({ online }: { online: boolean }) {
  return <Stamp tone={online ? "sage" : "signal"}>{online ? "ONLINE" : "OFFLINE"}</Stamp>;
}

function SectionEmpty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: "22px 14px", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", fontSize: 13.5, color: C.inkMute }}>{children}</div>;
}

function UnitCard({ u, now }: { u: UnitRow; now: number }) {
  const since = u.isOnline ? u.onlineSinceUtc : u.offlineSinceUtc;
  const sinceLabel = u.isOnline
    ? (since ? `online since ${fmtDateTime(since)}` : "online")
    : (since ? `offline since ${fmtDateTime(since)} (${durationSince(since, now) ?? "—"})` : "offline");
  return (
    <div style={{ background: C.paperWarm, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.ink, flex: 1, minWidth: 0 }}>{u.unitName}</span>
        <OnlinePill online={u.isOnline} />
      </div>
      <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, fontWeight: 600, marginTop: 4 }}>{sinceLabel}</div>
      <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginTop: 5, display: "flex", flexWrap: "wrap", gap: "3px 14px" }}>
        <span>heartbeat {ageText(u.lastHeartbeatUtc, now)}</span>
        <span>{u.network ?? "network unknown"}{u.signalPct != null ? ` · ${u.signalPct}%` : ""}</span>
        {u.satellites != null && <span>{u.satellites} satellites</span>}
      </div>
    </div>
  );
}

function FeedHealthRow({ label, feed, now, isFirst }: { label: string; feed: FeedRow; now: number; isFirst: boolean }) {
  const lastSuccessMs = feed.lastSuccessUtc ? new Date(feed.lastSuccessUtc).getTime() : null;
  const hasData = feed.lastAttemptUtc != null || feed.lastSuccessUtc != null;
  const ok = hasData && feed.lastOk !== false && lastSuccessMs != null && now - lastSuccessMs < FEED_STALE_MINUTES * 60_000;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{label}</div>
        <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginTop: 2 }}>
          {hasData ? `last success ${feed.lastSuccessUtc ? fmtDateTime(feed.lastSuccessUtc) : "never"}` : "no polls recorded"}
        </div>
        {!ok && (feed.lastErrorType || feed.lastMessage) && (
          <div style={{ fontFamily: F.body, fontSize: 12, color: C.signalDk, fontWeight: 600, marginTop: 3, overflowWrap: "anywhere" }}>
            {feed.lastErrorType ?? "error"}{feed.lastMessage ? `: ${feed.lastMessage}` : ""}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        {hasData ? <Stamp tone={ok ? "sage" : "signal"}>{ok ? "OK" : "FAILING"}</Stamp> : <Stamp tone="paper">NO DATA</Stamp>}
      </div>
    </div>
  );
}

export function LiveTrackerPage() {
  const navigate = useNavigate();
  const { me, loading: meLoading } = useCurrentUser();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [state, setState] = useState<StateResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [stateError, setStateError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Public web-page On/Off switch (live.nymr.co.uk home page). null = not loaded.
  const [pageEnabled, setPageEnabled] = useState<boolean | null>(null);
  const [pageBusy, setPageBusy] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const allowed = Boolean(me && (me.isAdmin || me.apps?.liveTracker));

  const load = useCallback(async () => {
    const [h, s, p] = await Promise.allSettled([
      apiFetch<HealthResponse>("/api/live-tracker/health"),
      apiFetch<StateResponse>("/api/live-tracker/state"),
      apiFetch<{ enabled: boolean }>("/api/live-tracker/page-state"),
    ]);
    if (h.status === "fulfilled") { setHealth(h.value); setHealthError(null); }
    else setHealthError(h.reason instanceof ApiError ? h.reason.message : "Couldn't load tracker health.");
    if (s.status === "fulfilled") { setState(s.value); setStateError(null); }
    else setStateError(s.reason instanceof ApiError ? s.reason.message : "Couldn't load the live map snapshot.");
    if (p.status === "fulfilled") { setPageEnabled(p.value.enabled); setPageError(null); }
    else setPageError(p.reason instanceof ApiError ? p.reason.message : "Couldn't read the web-page state.");
    setNow(Date.now());
  }, []);

  const togglePage = useCallback(async (next: boolean) => {
    if (!next && !window.confirm(
      "Turn the public Live Tracker web page OFF?\n\nlive.nymr.co.uk will show a “Not In Service” holding page to the public until you turn it back on."
    )) return;
    setPageBusy(true);
    setPageError(null);
    try {
      const res = await apiFetch<{ enabled: boolean }>("/api/live-tracker/page-state", {
        method: "POST",
        body: JSON.stringify({ enabled: next }),
      });
      setPageEnabled(res.enabled);
    } catch (e) {
      setPageError(e instanceof ApiError ? e.message : "Couldn't update the web-page state.");
    } finally {
      setPageBusy(false);
    }
  }, []);

  // Poll both endpoints every 30s while the page is open.
  useEffect(() => {
    if (!allowed) return;
    void load();
    const t = window.setInterval(() => { void load(); }, POLL_MS);
    return () => window.clearInterval(t);
  }, [allowed, load]);

  const stations = useMemo(
    () => (state?.stations ?? []).filter((s): s is { name: string; positionPct: number } => typeof s.name === "string" && typeof s.positionPct === "number"),
    [state],
  );
  const nearestStation = useCallback((pct: number | null | undefined): string | null => {
    if (pct == null || stations.length === 0) return null;
    let best: { name: string; d: number } | null = null;
    for (const s of stations) {
      const d = Math.abs(s.positionPct - pct);
      if (!best || d < best.d) best = { name: s.name, d };
    }
    return best ? best.name : null;
  }, [stations]);

  if (meLoading) return <PermissionLoading title="Live Tracker" onBack={() => navigate("/apps")} />;

  if (!allowed) {
    return (
      <div>
        <TopBar title="Live Tracker" onBack={() => navigate("/apps")} />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 6 }}>This app is for admins and HR admins.</div>
            <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, fontWeight: 600, lineHeight: 1.45 }}>Ask a system administrator if you need access to the tracker fleet health view.</div>
            <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={() => navigate("/apps")}>Back to apps</Button>
          </Card>
        </div>
      </div>
    );
  }

  const trains = state?.trains ?? [];

  return (
    <div>
      <TopBar title="Live Tracker" sub="GPS fleet health & live trains" onBack={() => navigate("/apps")} />
      <div style={{ padding: "14px 16px 28px", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* ── Header: link out to the public live map ── */}
        <a
          href={LIVE_MAP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open the live train tracker on live.nymr.co.uk (opens in a new tab)"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            fontFamily: F.body, fontWeight: 800, fontSize: 15, padding: "12px 16px", minHeight: 48,
            borderRadius: 12, textDecoration: "none", background: C.brassDk, color: C.brassGlow,
            boxShadow: "inset 0 1px 0 rgba(244,226,168,0.3), 0 2px 6px rgba(122,84,11,0.15)",
          }}
        >
          Open live map <span aria-hidden="true">↗</span>
        </a>

        {/* ── Public web page On/Off ── */}
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Eyebrow>Public web page</Eyebrow>
              <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginTop: 3 }}>live.nymr.co.uk</div>
            </div>
            {pageEnabled == null
              ? <Stamp tone="paper">…</Stamp>
              : <Stamp tone={pageEnabled ? "sage" : "signal"}>{pageEnabled ? "LIVE" : "NOT IN SERVICE"}</Stamp>}
          </div>
          {pageEnabled != null && (
            <Button
              variant={pageEnabled ? "danger" : "primary"}
              fullWidth
              disabled={pageBusy}
              style={{ marginTop: 12 }}
              onClick={() => togglePage(!pageEnabled)}
            >
              {pageBusy ? "Saving…" : pageEnabled ? "Turn web page OFF" : "Turn web page ON"}
            </Button>
          )}
          <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkMute, fontWeight: 600, marginTop: 8, lineHeight: 1.45 }}>
            {pageEnabled === false
              ? "The public page shows a “Not In Service” holding message. The staff fleet view below still works, and /admin/live is unaffected."
              : "Turning this off replaces the public live.nymr.co.uk home page with a “Not In Service” holding page. Takes effect within about 15 seconds."}
          </div>
          {pageError && <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700, fontSize: 12.5, marginTop: 8 }}>{pageError}</div>}
        </Card>

        {healthError && (
          <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{healthError}</div></Card>
        )}

        {/* ── Fleet health strip ── */}
        <Card>
          <Eyebrow>Tracker fleet</Eyebrow>
          {!health && !healthError && <SectionEmpty>Loading tracker health…</SectionEmpty>}
          {health && (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <span style={{ fontFamily: F.serif, fontSize: 24, fontWeight: 700, color: health.summary.onlineUnits === health.summary.totalUnits ? C.sageDk : C.signalDk }}>
                  {health.summary.onlineUnits} of {health.summary.totalUnits}
                </span>
                <span style={{ fontFamily: F.body, fontSize: 13.5, color: C.inkSoft, fontWeight: 600 }}>trackers online</span>
              </div>
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.inkMute, fontWeight: 600, marginTop: 2 }}>updated {fmtTime(health.generatedAt)} · refreshes every 30s</div>
              <Hairline style={{ margin: "12px 0" }} />
              {health.units.length === 0
                ? <SectionEmpty>No tracker units recorded yet.</SectionEmpty>
                : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {health.units.map((u) => <UnitCard key={u.unitId} u={u} now={now} />)}
                  </div>}
            </>
          )}
        </Card>

        {/* ── Feed health ── */}
        {health && (
          <Card padded={false}>
            <div style={{ padding: "12px 14px 4px" }}><Eyebrow>Feed health</Eyebrow></div>
            <FeedHealthRow label="GPS platform" feed={health.feed.tracker} now={now} isFirst />
            <FeedHealthRow label="Real Time Trains" feed={health.feed.rtt} now={now} isFirst={false} />
          </Card>
        )}

        {/* ── Live trains snapshot ── */}
        <Card padded={false}>
          <div style={{ padding: "12px 14px 4px", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1 }}><Eyebrow>Live trains</Eyebrow></div>
            {state?.mode === "sim" && <Stamp tone="heather">SIMULATION</Stamp>}
          </div>
          {stateError && <div style={{ padding: "10px 14px", color: C.signalDk, fontFamily: F.body, fontWeight: 700, fontSize: 13 }}>{stateError}</div>}
          {!state && !stateError && <SectionEmpty>Loading the live map snapshot…</SectionEmpty>}
          {state && (trains.length === 0
            ? <SectionEmpty>No trains on the map right now.</SectionEmpty>
            : trains.map((t, i) => {
                const near = nearestStation(t.positionPct);
                const isGps = (t.source ?? "").toLowerCase() === "gps";
                const sourceTag = isGps ? "GPS" : (t.isEstimated ? "RTT (est)" : "RTT");
                const delay = t.delayMinutes != null && t.delayMinutes !== 0
                  ? (t.delayMinutes > 0 ? ` · ${t.delayMinutes}m late` : ` · ${Math.abs(t.delayMinutes)}m early`)
                  : "";
                return (
                  <div key={t.id ?? i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>
                        {t.locoCode ?? t.matchedHeadcode ?? "Unknown"}
                        {t.matchedHeadcode && t.locoCode && <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600 }}> · {t.matchedHeadcode}</span>}
                      </div>
                      <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft, fontWeight: 600, marginTop: 2 }}>
                        {t.direction ?? "—"}{t.positionPct != null ? ` · ${Math.round(t.positionPct)}%` : ""}{near ? ` (near ${near})` : ""}
                      </div>
                      <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginTop: 2 }}>
                        {t.status ?? "—"}{delay}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Stamp tone={isGps ? "sage" : "heather"}>{sourceTag}</Stamp>
                    </div>
                  </div>
                );
              }))}
        </Card>

        {/* ── On/off history (last 7 days) ── */}
        {health && (
          <Card padded={false}>
            <div style={{ padding: "12px 14px 4px" }}><Eyebrow>On/off history · last 7 days</Eyebrow></div>
            {health.events.length === 0
              ? <SectionEmpty>No online/offline transitions in the last 7 days.</SectionEmpty>
              : <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F.body, fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Time", "Unit", "Event", "Details"].map((hCol) => (
                          <th key={hCol} style={{ textAlign: "left", padding: "8px 14px", fontFamily: F.mono, fontSize: 10.5, letterSpacing: 1, color: C.inkMute, borderBottom: `1px solid ${C.hairlineLt}`, whiteSpace: "nowrap" }}>{hCol.toUpperCase()}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {health.events.map((e, i) => (
                        <tr key={`${e.unitId}-${e.eventUtc}-${i}`}>
                          <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.hairlineLt}`, whiteSpace: "nowrap", fontFamily: F.mono, fontSize: 12, color: C.inkSoft, fontWeight: 600 }}>{fmtDateTime(e.eventUtc)}</td>
                          <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.hairlineLt}`, fontWeight: 700, color: C.ink, whiteSpace: "nowrap" }}>{e.unitName}</td>
                          <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.hairlineLt}` }}>
                            <OnlinePill online={e.eventType.toLowerCase() === "online"} />
                          </td>
                          <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.hairlineLt}`, color: C.inkSoft, fontWeight: 600, fontSize: 12.5 }}>
                            {e.details ?? (e.heartbeatAgeSeconds != null ? `heartbeat age ${Math.round(e.heartbeatAgeSeconds / 60)}m` : "—")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
          </Card>
        )}
      </div>
    </div>
  );
}

export default LiveTrackerPage;
